import { type RefObject, useRef } from "react";
import { ChevronRight } from "lucide-react";
import { UPCOMING_MODELS } from "../data/upcoming-models";
import { useI18n } from "../hooks/use-i18n";
import { ui } from "../lib/localization";
import {
  organizationLogoCompanies,
  organizationName,
  type OrganizationIndex
} from "../lib/organizations";
import { CometBorder } from "./CometBorder";
import { CuratedSectionPanel } from "./CuratedSectionPanel";
import { RotatingCompany } from "./RotatingCompany";
import type { SmoothScrollController } from "./SmoothScroll";
import { TiltedSpotlightCard } from "./react-bits/TiltedSpotlightCard";
import { VendorMark } from "./VendorMark";

interface UpcomingSectionProps {
  open: boolean;
  onToggle: () => void;
  onMotionComplete: () => void;
  scrollElementRef: RefObject<HTMLElement | null>;
  scrollControllerRef: RefObject<SmoothScrollController | null>;
  organizations: OrganizationIndex;
}

export function UpcomingSection({
  open,
  onToggle,
  onMotionComplete,
  scrollElementRef,
  scrollControllerRef,
  organizations
}: UpcomingSectionProps) {
  const { language } = useI18n();
  const expandFromTop = useRef<number | null>(null);

  return (
    <section
      className={`curated-section curated-section--upcoming${open ? " curated-section--open" : ""}`}
      aria-labelledby="upcoming-section-title"
    >
      <button
        className="curated-section__toggle"
        type="button"
        aria-expanded={open}
        aria-controls="upcoming-model-grid"
        aria-label={ui(language, open ? "collapseUpcoming" : "expandUpcoming")}
        onClick={(event) => {
          if (!open) {
            expandFromTop.current = event.currentTarget.getBoundingClientRect().top;
          }
          onToggle();
        }}
      >
        <span id="upcoming-section-title">{ui(language, "upcoming")}</span>
        <i aria-hidden="true" />
        <ChevronRight aria-hidden="true" size={15} strokeWidth={1.8} />
      </button>

      <CuratedSectionPanel
        id="upcoming-model-grid"
        open={open}
        onMotionComplete={onMotionComplete}
        scrollElementRef={scrollElementRef}
        scrollControllerRef={scrollControllerRef}
        expandFromTopRef={expandFromTop}
      >
        <div className="model-grid curated-section__grid">
          {UPCOMING_MODELS.map((model, index) => {
            const companyLabel = model.companyIds
              .map((organizationId) =>
                organizationName(organizationId, organizations, language)
              )
              .join(" / ");
            const logoCompanyId =
              model.companyIds[model.companyIds.length - 1] ?? "";
            const logoCompany =
              organizations.get(logoCompanyId)?.logoCompany ?? companyLabel;

            return (
              <TiltedSpotlightCard
                dataModelId={`upcoming:${model.modelId}`}
                key={model.modelId}
              >
                <article
                  className="model-card upcoming-card"
                  aria-label={`${model.modelId}, ${companyLabel}, ${model.expectedRelease[language]}`}
                >
                  {model.outlined ? (
                    // Stagger the comets so neighbouring cards do not run in
                    // step, and light them up one after another on arrival.
                    <CometBorder
                      delaySeconds={index * -1.3}
                      entranceDelaySeconds={index * 0.15}
                    />
                  ) : null}
                  {model.rotateCompanies ? (
                    <RotatingCompany
                      entries={model.companyIds.map((organizationId) => ({
                        label: organizationName(organizationId, organizations, language),
                        logoCompany:
                          organizations.get(organizationId)?.logoCompany ??
                          organizationName(organizationId, organizations, language)
                      }))}
                      modelName={model.logoModelName ?? model.modelId}
                      title={model.modelId}
                    />
                  ) : (
                    <span className="model-card__heading">
                      <VendorMark
                        company={logoCompany}
                        modelName={model.logoModelName ?? model.modelId}
                        organizationLogoCompany={organizationLogoCompanies(
                          logoCompanyId,
                          organizations
                        )}
                      />
                      <span className="model-card__title-wrap">
                        <strong>{model.modelId}</strong>
                        <small>{companyLabel}</small>
                      </span>
                    </span>
                  )}

                  <span className="upcoming-card__forecast">
                    <small>{ui(language, "expectedRelease")}</small>
                    <strong>{model.expectedRelease[language]}</strong>
                  </span>
                </article>
              </TiltedSpotlightCard>
            );
          })}
        </div>
      </CuratedSectionPanel>
    </section>
  );
}
